import { useMutation } from "@apollo/client/react";
import type { DocumentNode } from "graphql";
import { Copy, CopyCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

/**
 * „Kopijuoti AI" — a review queue as markdown, on the clipboard, ready to paste
 * into a conversation with whoever is going to fix it.
 *
 * Shared by /admin/errors, /admin/flagged and /admin/performance. Not because the
 * code is long, but because the wording has to match: the toast, the hover and
 * the "already handed over" sentence are the same promise on all three pages, and
 * three copies of it drift. What differs is only which mutation composes the
 * markdown.
 *
 * **The markdown is composed on the server**, so the button and the matching rake
 * task cannot describe the same queue differently — and it is written in
 * **English**, even though the app is Lithuanian: the reader is an AI being asked
 * to fix something, so the report's own prose is instructions to it and only the
 * quoted content (a question, a child's own sentence) stays Lithuanian. The
 * button, the toast and this hover are Lithuanian, because those are read by the
 * grown-up pressing them.
 *
 * **A mutation and not a query, because copying changes the queue.** What went is
 * stamped `copiedAt`, and that is what tells the next reader they have already
 * handed this one over. A timestamp rather than a tick, because "copied" goes
 * stale: something sent off three weeks ago and still in the queue is not
 * handled — which is why the hover carries the date and not just the fact.
 */
export function CopyForAi({
  document,
  variables,
  pick,
  label,
  hint,
  copiedAt,
  onCopied,
  variant = "default",
  size = "sm",
}: {
  /** The mutation that composes the markdown and records the handover. */
  document: DocumentNode;
  variables: Record<string, unknown>;
  /** Pulls `{ report, errors }` out of that mutation's own payload. */
  pick: (data: any) => { report?: string | null; errors?: string[] } | undefined;
  /**
   * Omitted in a row, where a column of labelled buttons is noise and the
   * header's own labelled button is what names the action.
   */
  label?: string;
  /** What the button does, for the hover. */
  hint?: string;
  /** When this was last handed over, if it was. Shown on hover. */
  copiedAt?: string | null;
  /** For a caller whose list cannot be updated by record — see FlaggedContentPage. */
  onCopied?: () => void;
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "xs";
}) {
  const [run, { loading }] = useMutation(document);

  const copy = async () => {
    try {
      const result: any = await run({ variables });
      const payload = pick(result?.data);
      if (payload?.errors?.length) {
        toast.error(payload.errors[0]);
        return;
      }
      if (!payload?.report) {
        toast.error("Nepavyko paruošti ataskaitos");
        return;
      }
      await navigator.clipboard.writeText(payload.report);
      toast.success("Nukopijuota — įklijuok pokalbyje su AI");
      onCopied?.();
    } catch {
      toast.error("Nepavyko nukopijuoti");
    }
  };

  const small = size === "xs";
  // A date is the whole point: "handed over" without a when says nothing about
  // whether it is still fresh.
  const title = copiedAt
    ? `Jau perduota AI ${ago(copiedAt)} (${absolute(copiedAt)}). ${hint ?? ""}`.trim()
    : hint;
  const Icon = copiedAt ? CopyCheck : Copy;
  const iconSize = small ? "h-3.5 w-3.5" : "h-4 w-4";
  const gap = label ? (small ? "mr-1" : "mr-2") : "";

  return (
    <Button
      variant={copiedAt && variant === "default" ? "outline" : variant}
      size={small ? "sm" : size}
      onClick={copy}
      disabled={loading}
      title={title}
      aria-label={label || "Kopijuoti AI"}
      className={small ? "h-7 px-2 text-xs" : undefined}
    >
      {loading ? (
        <Loader2 className={`animate-spin ${iconSize} ${gap}`} />
      ) : (
        <Icon className={`${iconSize} ${gap} ${copiedAt ? "text-muted-foreground" : ""}`} />
      )}
      {label}
    </Button>
  );
}

/**
 * "prieš 3 min." beats a timestamp on a hover: the question being answered is
 * whether the handover is still recent, and that is a distance rather than a
 * date. The absolute time is in the same tooltip for when it is not.
 */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "ką tik";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `prieš ${minutes} min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `prieš ${hours} val.`;
  return `prieš ${Math.floor(hours / 24)} d.`;
}

function absolute(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat("lt-LT", { dateStyle: "short", timeStyle: "short" }).format(date);
}
